import type { FC } from 'react'
import type { PlacedOrder } from '@/types'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { animationDelay } from '@/lib/motion'
import { formatClock, formatDateKey } from '@/lib/hours'
import { firstName, formatMoney, formatNumber, pluralize } from '@/lib/format'
import { ButtonLink } from '@/components/generals/button'

import { SHELL, bakery } from '@/constants/site'

interface OrderConfirmationProps {
    order: PlacedOrder
    onStartAgain: () => void
}

export const OrderConfirmation: FC<OrderConfirmationProps> = ({ order, onStartAgain }) => {
    const titleRef = useRef<HTMLHeadingElement>(null)
    const day = formatDateKey(order.pickupDate, { weekday: 'long', month: 'long', day: 'numeric' })
    const time = formatClock(order.pickupTime)

    // Moves screen reader and keyboard focus to the confirmation once the order is placed.
    useEffect(() => {
        titleRef.current?.focus({ preventScroll: true })
    }, [])

    return (
        <section aria-labelledby='confirmation-title' className={cn(SHELL, 'grid gap-14 pt-12 pb-24 lg:grid-cols-12 lg:gap-10 lg:pt-20 lg:pb-32')}>
            <div className='lg:col-span-6'>
                <p className='ld-rise label-caps text-amber'>سفارش دریافت شد · <span dir='ltr'>{order.reference}</span></p>

                <h1 ref={titleRef} id='confirmation-title' className='ld-rise mt-5 font-heading text-[3rem] leading-[0.98] font-normal outline-none sm:text-6xl lg:text-[4.5rem]' style={animationDelay(100)} tabIndex={-1}>
                    <bdi>{firstName(order.name)}</bdi> عزیز، ممنونیم.
                    <span className='block italic'>سفارشتان ثبت شد.</span>
                </h1>

                <p className='ld-rise mt-6 max-w-md text-[1.0625rem] leading-relaxed text-crust-soft' style={animationDelay(200)}>
                    سفارشتان را بسته‌بندی می‌کنیم و آماده نگه می‌داریم. پشت پیشخوان نام یا کد پیگیری‌تان را بگویید و هنگام تحویل پرداخت کنید.
                </p>

                <dl className='ld-rise mt-10 grid gap-8 border-t border-line pt-8 sm:grid-cols-2' style={animationDelay(300)}>
                    <div>
                        <dt className='label-caps text-crust-soft'>تحویل حضوری</dt>
                        <dd className='mt-3 font-heading text-[1.75rem] leading-tight'>{day}</dd>
                        <dd className='mt-1 text-[1.0625rem]'>{time}</dd>
                    </div>

                    <div>
                        <dt className='label-caps text-crust-soft'>نشانی</dt>
                        <dd className='mt-3 text-[1.0625rem] leading-relaxed'>
                            <bdi>{bakery.street}</bdi>
                            <br />
                            <bdi>{bakery.locality}, {bakery.region} {bakery.postalCode}</bdi>
                        </dd>
                        <dd className='mt-1 text-sm text-crust-soft'>{bakery.nearby}</dd>
                    </div>
                </dl>

                <div className='ld-rise mt-10 flex flex-wrap items-center gap-3' style={animationDelay(400)}>
                    <ButtonLink href={bakery.directionsUrl} variant='outline'>
                        مسیریابی
                        <ArrowUpRight className='size-4 rtl:-scale-x-100' aria-hidden='true' />
                    </ButtonLink>

                    <ButtonLink href='/' variant='dark'>بازگشت به صفحه‌ی اصلی</ButtonLink>
                </div>

                <p className='mt-8 text-sm leading-relaxed text-crust-soft'>
                    می‌خواهید چیزی را تغییر دهید؟ با شماره‌ی <a href={bakery.phoneHref} dir='ltr' className='underline decoration-line-strong underline-offset-4 hover:decoration-foreground'>{bakery.phone}</a> تماس بگیرید و کد پیگیری <span dir='ltr'>{order.reference}</span> را بگویید.
                </p>
            </div>

            <aside className='ld-rise self-start border border-line p-6 sm:p-8 lg:col-span-5 lg:col-start-8' style={animationDelay(250)} aria-labelledby='confirmation-summary-title'>
                <div className='flex items-baseline justify-between gap-4'>
                    <h2 id='confirmation-summary-title' className='font-heading text-[2rem] leading-none font-normal'>خلاصه‌ی سفارش</h2>
                    <p className='text-sm text-crust-soft'>{pluralize(order.itemCount, 'عدد')}</p>
                </div>

                <ul className='mt-6 border-t border-line'>
                    {order.items.map(line => (
                        <li key={line.slug} className='flex items-baseline justify-between gap-4 border-b border-line py-3.5 text-[0.9375rem]'>
                            <span>
                                <span className='text-crust-soft tabular-nums'>{formatNumber(line.quantity)} ×</span> {line.name}
                            </span>
                            <span className='tabular-nums'>{formatMoney(line.lineTotal)}</span>
                        </li>
                    ))}
                </ul>

                <div className='flex items-baseline justify-between gap-4 pt-4'>
                    <p>جمع کل</p>
                    <p className='font-heading text-[2rem] leading-none tabular-nums'>{formatMoney(order.total)}</p>
                </div>

                <p className='mt-1 text-end text-xs text-crust-soft'>هنگام تحویل، پشت پیشخوان پرداخت می‌شود.</p>

                <div className='mt-8 border-t border-line pt-6 text-sm leading-relaxed text-crust-soft'>
                    <p>
                        می‌خواهید چیز دیگری هم بردارید؟{' '}
                        <button type='button' onClick={onStartAgain} className='cursor-pointer text-foreground underline decoration-line-strong underline-offset-4 hover:decoration-foreground focus-visible:outline-2 focus-visible:outline-amber'>
                            یک سفارش تازه ثبت کنید
                        </button>
                        {' '}یا <Link href='/menu' className='text-foreground underline decoration-line-strong underline-offset-4 hover:decoration-foreground'>منوی کامل</Link> را ببینید.
                    </p>
                </div>
            </aside>
        </section>
    )
}
